import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Manage an ordered array of strings in the URL query via repeated keys.
 * Example URL: ?v=abc&v=123&v=https%3A%2F%2Fexample.com
 */
export function useArrayQueryParam(key: string) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Read current values (URLSearchParams returns decoded strings)
  const items = useMemo(() => {
    // Preserves order of appearance in the URL
    return searchParams.getAll(key);
  }, [searchParams, key]);

  // Internal: write a full new array for this key while preserving other params
  const _write = useCallback(
    async (next: string[]) => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete(key); // clear existing
      next.forEach((val) => nextParams.append(key, val)); // order matters
      await navigate({ search: nextParams.toString() }, { replace: true });
    },
    [navigate, searchParams, key],
  );

  const setItems = useCallback((next: string[]) => _write(next), [_write]);

  const clear = useCallback(() => _write([]), [_write]);

  const push = useCallback((val: string) => _write([...items, val]), [_write, items]);

  const insertAt = useCallback(
    (index: number, val: string) => {
      const i = Math.max(0, Math.min(index, items.length));
      const next = items.slice();
      next.splice(i, 0, val);
      return _write(next);
    },
    [_write, items],
  );

  const replaceAt = useCallback(
    (index: number, val: string) => {
      if (index < 0 || index >= items.length) return _write(items);
      const next = items.slice();
      next[index] = val;
      return _write(next);
    },
    [_write, items],
  );

  const removeAt = useCallback(
    (index: number) => {
      if (index < 0 || index >= items.length) return _write(items);
      const next = items.slice();
      next.splice(index, 1);
      return _write(next);
    },
    [_write, items],
  );

  const removeValue = useCallback(
    (val: string, opts?: { all?: boolean }) => {
      const { all = false } = opts || {};
      const next = all
        ? items.filter((v) => v !== val)
        : (() => {
            const idx = items.indexOf(val);
            if (idx === -1) return items;
            const n = items.slice();
            n.splice(idx, 1);
            return n;
          })();
      return _write(next);
    },
    [_write, items],
  );

  const move = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex || fromIndex < 0 || fromIndex >= items.length) return _write(items);

      const clampedTo = Math.max(0, Math.min(toIndex, items.length - 1));
      const next = items.slice();
      const [moved] = next.splice(fromIndex, 1);
      next.splice(clampedTo, 0, moved);
      return _write(next);
    },
    [_write, items],
  );

  return {
    items, // string[]
    setItems, // (arr) => void
    push, // (val) => void
    insertAt, // (index, val) => void
    replaceAt, // (index, val) => void
    removeAt, // (index) => void
    removeValue, // (val, { all?: boolean }) => void
    move, // (fromIndex, toIndex) => void
    clear, // () => void
  };
}
